import type { QueryClient } from "@tanstack/react-query";
import { ExportedMessageRepository } from "@assistant-ui/react";

import {
    conversationDetailQueryOptions,
    getConversation,
    getConversationTitle,
} from "@/api";
import { isAxiosNotFoundError } from "@/api/errors";
import { queryClient as appQueryClient } from "@/api/queryClient";
import type { IReportVersionSummary } from "@/api/reports/reports.types";
import {
    conversationReportsQueryKey,
    readConversationReports,
} from "@/hooks/useConversationReports";

import {
    DEFAULT_CONVERSATION_TITLE,
    ownedConversationsQueryOptions,
} from "../adapters/api-thread-list-adapter";
import { REPORT_PART_WAIT_MS } from "../adapters/chat-model-adapter";
import {
    fetchQueryFollowingReplacement,
    refreshQueryAfterWrite,
} from "./conversationQueries";
import { invalidateConversationLists } from "./invalidateConversationLists";
import { convertMessages } from "./convert-messages";

export const RECOVERY_COMPLETION_RETRY_MS = 1_000;
const RECOVERY_IMPORT_ATTEMPTS = 8;
const RECOVERED_TITLE_ATTEMPTS = 40;
const RECOVERED_TITLE_MAX_RETRY_MS = 15_000;

function retryDelay(attempt: number): number {
    return Math.min(
        RECOVERY_COMPLETION_RETRY_MS * 2 ** attempt,
        RECOVERED_TITLE_MAX_RETRY_MS
    );
}

function pause(milliseconds: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });
}

function isDefaultConversationTitle(title: string): boolean {
    return title.trim() === "" || title === DEFAULT_CONVERSATION_TITLE;
}

/**
 * A failed report read, or one still unanswered after the report wait, leaves
 * the history without report cards; it never fails or holds the load. An
 * unanswered read is cancelled at the wait, so the next import asks again.
 */
async function readReportsForImport(
    conversationId: number | string
): Promise<IReportVersionSummary[]> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        return await Promise.race([
            readConversationReports(
                appQueryClient,
                Number(conversationId)
            ).catch((): IReportVersionSummary[] => []),
            new Promise<IReportVersionSummary[]>((resolve) => {
                timer = setTimeout(() => {
                    void appQueryClient.cancelQueries({
                        queryKey: conversationReportsQueryKey(
                            Number(conversationId)
                        ),
                        exact: true,
                    });
                    resolve([]);
                }, REPORT_PART_WAIT_MS);
            }),
        ]);
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Fetch a conversation and its report rows and shape them into an
 * assistant-ui repository.
 *
 * Shared by the history adapter's `load()` and by the recovery completion path
 * so a recovered turn is rendered through exactly the same conversion as a
 * reload -- two conversions would drift.
 */
export async function loadConversationImport(
    conversationId: number | string,
    { reuseDetailFor = 0 }: { reuseDetailFor?: number } = {}
): Promise<{
    repository: ExportedMessageRepository;
    title: string;
}> {
    const [detail, reportRows] = await Promise.all([
        reuseDetailFor > 0
            ? fetchQueryFollowingReplacement(appQueryClient, {
                  ...conversationDetailQueryOptions(String(conversationId)),
                  staleTime: reuseDetailFor,
              })
            : getConversation(conversationId),
        readReportsForImport(conversationId),
    ]);
    return {
        repository: ExportedMessageRepository.fromArray(
            convertMessages(
                detail.chat_messages,
                detail.chat_attachments,
                reportRows
            )
        ),
        title: detail.title,
    };
}

export async function loadRecoveredConversationImport(
    conversationId: number | string
): Promise<Awaited<ReturnType<typeof loadConversationImport>>> {
    let lastError: unknown;
    for (let attempt = 0; attempt < RECOVERY_IMPORT_ATTEMPTS; attempt += 1) {
        try {
            return await loadConversationImport(conversationId);
        } catch (error) {
            if (isAxiosNotFoundError(error)) throw error;
            lastError = error;
            if (attempt + 1 < RECOVERY_IMPORT_ATTEMPTS) {
                await pause(retryDelay(attempt));
            }
        }
    }
    throw lastError;
}

export async function waitForRecoveredConversationTitle(
    conversationId: number | string,
    initialTitle: string
): Promise<string | undefined> {
    if (!isDefaultConversationTitle(initialTitle)) return initialTitle;
    for (let attempt = 0; attempt < RECOVERED_TITLE_ATTEMPTS; attempt += 1) {
        await pause(retryDelay(attempt));
        try {
            const title = await getConversationTitle(conversationId);
            if (!isDefaultConversationTitle(title)) return title;
        } catch (error) {
            if (isAxiosNotFoundError(error)) return undefined;
            continue;
        }
    }
    return undefined;
}

export async function loadConversationRepository(
    conversationId: number | string,
    options?: { reuseDetailFor?: number }
): Promise<ExportedMessageRepository> {
    return (await loadConversationImport(conversationId, options)).repository;
}

/**
 * Cache work every finished turn must do, whichever path finished it.
 *
 * Both the live stream's `onComplete` and a recovered turn's completion call
 * this, so their invalidations cannot drift apart.
 */
export function handleChatTurnCompleted(queryClient: QueryClient): void {
    queryClient.invalidateQueries({
        queryKey: ["frontend-document-extracted-fields"],
    });
    queryClient.invalidateQueries({ queryKey: ["template-renders"] });
    queryClient.invalidateQueries({ queryKey: ["conversation-reports"] });
    refreshConversationListsAfterTurn(queryClient);
}

/** Call once the server has saved a turn's message, which moves its chat's last activity. */
export function refreshConversationListsAfterTurn(
    queryClient: QueryClient
): void {
    void invalidateConversationLists(queryClient);
    void refreshQueryAfterWrite(queryClient, ownedConversationsQueryOptions);
}
